import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse } from '@boma/client-shared';
import {
  IIndependentInventoryIn,
  IInventory,
  IInventoryOut,
  IInventoryTransferOrderCreate,
  IOrderCreate,
} from '@boma/supplier-portal/shared/entities';
import { HelperFunctionsService } from '@boma/supplier-portal/shared/services';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  private base: string;
  private version: string;

  constructor(
    private helperService: HelperFunctionsService,
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  loadInventory(
    entityId: string,
    locationId: string = '',
    productId: string = '',
    page: string = '1',
    perPage: string = '25',
    startDate: string = '',
    endDate: string = '',
    query: string = '',
    direction: string,
    locationType: string,
    eventType: string
  ) {
    const params = {
      locationId: locationId,
      productId: productId,
      page: page,
      perPage: perPage,
      startDate: startDate,
      endDate: endDate,
      query: query,
      direction: direction,
      eventType: eventType,
    };

    if (locationType) {
      params['locationType'] = locationType;
    }

    this.helperService.sanitizeParams(params);

    return this.httpClient.get<IPaginatedResponse<IInventory>>(
      `${this.base}api/v${this.version}/${entityId}/Inventory`,
      {
        params,
      }
    );
  }

  loadInventoryActionDetails(entityId: string, inventoryActionId: string) {
    return this.httpClient.get<IInventory>(
      `${this.base}api/v${this.version}/${entityId}/Inventory/${inventoryActionId}`
    );
  }

  inventoryOut(entityId: string, details: IInventoryOut) {
    return this.httpClient.post<IInventory>(
      `${this.base}api/v${this.version}/${entityId}/Inventory/out`,
      {
        ...details,
      }
    );
  }

  inventoryIn(entityId: string, details: IIndependentInventoryIn) {
    return this.httpClient.post<IInventory>(
      `${this.base}api/v${this.version}/${entityId}/Inventory/in`,
      {
        ...details,
      }
    );
  }

  inventoryInUpdate(
    entityId: string,
    id: string,
    details: IIndependentInventoryIn
  ) {
    return this.httpClient.put<IInventory>(
      `${this.base}api/v${this.version}/${entityId}/Inventory/in/${id}`,
      {
        ...details,
      }
    );
  }

  inventoryOutUpdate(entityId: string, id: string, details: IInventoryOut) {
    return this.httpClient.put<IInventory>(
      `${this.base}api/v${this.version}/${entityId}/Inventory/out/${id}`,
      {
        ...details,
      }
    );
  }

  // inventoryTransfer(entityId: string, details: IInventoryTransferRequest) {
  //   return this.httpClient.post<IInventoryTransferResponse>(
  //     `${this.base}api/v${this.version}/${entityId}/Inventory/transfer`,
  //     {
  //       ...details,
  //     }
  //   );
  // }

  inventoryOrderTransfer(
    entityId: string,
    details: IInventoryTransferOrderCreate,
    idempotencyKey?: string
  ) {
    const params = {
      receivingLocationId: details.receivingLocationId,
      supplyingLocationId: details.supplyingLocationId,
      items: details.items,
      isDirect: details.isDirect,
      requireHandshake: details.requireHandshake,
      comment: details.comment,
    };

    let headers = new HttpHeaders();
    if (idempotencyKey || details?.idempotencyKey) {
      const key = idempotencyKey || details.idempotencyKey;
      headers = headers.append('boma-idempotency-key', key);
    }

    return this.httpClient.post<IOrderCreate>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${details.supplyingLocationId}/Transfer`,
      params,
      {
        headers,
      }
    );
  }

  getStockLevels(
    entityId: string, //required
    locationId: string = null, // TODO upgrade to array
    productId: string = null, // TODO upgrade to array
    page: string = '1',
    perPage: string = '500'
  ) {
    const params = {
      entityId: entityId,
    };

    let locationIds = [];
    if (locationId) {
      locationIds = [locationId];
    }
    const productIds = [];
    if (productId) {
      productIds.push(productId);
    }

    params['page'] = page;
    params['perPage'] = perPage;

    return this.httpClient.post<any>(
      `${this.base}api/v${this.version}/${entityId}/Stock/Summary/Report`,
      {
        entityId,
        locationType: null,
        locationIds: locationIds,
        categoryIds: null,
        brandId: null,
        productIds: productIds,
        page: Number(page),
        perPage: Number(perPage),
      }
    );
  }

  getStockForSale(
    entityId: string, //required
    locationId: string = null, // TODO upgrade to array
    productId: string = null, // TODO upgrade to array
    customerEntityId: string = null,
    onlyProductsForPurchase: boolean = false,
    page: string = '1',
    perPage: string = '500'
  ) {
    const params = {
      entityId: entityId,
    };

    let locationIds = [];
    if (locationId) {
      locationIds = [locationId];
    }
    const productIds = [];
    if (productId) {
      productIds.push(productId);
    }

    params['page'] = page;
    params['perPage'] = perPage;

    return this.httpClient.post<any>(
      `${this.base}api/v${this.version}/${entityId}/Stock/Summary`,
      {
        entityId,
        locationType: null,
        locationIds: locationIds,
        categoryIds: null,
        brandId: null,
        productIds: productIds,
        page: Number(page),
        perPage: Number(perPage),
        customerEntityId,
        onlyProductsForPurchase,
      }
    );
  }
}
